import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { User, ArrowLeft, Bell } from "lucide-react";
import { useAuth } from "@/hooks/useAuthState";
import { Button } from "@/components/ui/button";
import { apiRequest } from "@/lib/queryClient";
import prizeDropLogo from "@assets/logo 7_1756706183202.png";

export function MobileTopBar() {
  const { user } = useAuth();
  const [location] = useLocation();
  const [inboxOpen, setInboxOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  
  // Check if we're on the dashboard
  const isDashboard = location === "/m" || location === "/m/";

  // Only show for authenticated users
  if (!user) {
    return null;
  }

  return (
    <div className="md:hidden sticky top-0 z-40 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
      <div className="flex items-center justify-between px-4 py-3">
        {/* Logo (Dashboard) or Back Button (Other pages) */}
        {isDashboard ? (
          <Link href="/m">
            <div className="flex items-center cursor-pointer">
              <img 
                src={prizeDropLogo} 
                alt="Prize Drop" 
                className="h-8 w-auto object-contain"
              />
            </div>
          </Link>
        ) : (
          <button
            onClick={() => window.history.back()}
            className="flex items-center justify-center h-10 w-10 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            data-testid="button-back"
            aria-label="Go back"
          >
            <ArrowLeft className="h-6 w-6 text-gray-700 dark:text-gray-300" />
          </button>
        )}

        {/* Notifications + Profile Icons */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="relative inline-flex items-center justify-center h-10 w-10 aspect-square rounded-full border border-gray-300 bg-white hover:bg-gray-50 dark:bg-gray-900 dark:border-gray-700 dark:hover:bg-gray-800 transition-colors p-0"
            aria-label="Notifications"
            onClick={() => setInboxOpen(true)}
          >
            <Bell className="h-5 w-5 text-gray-700 dark:text-gray-200" />
            {unreadCount > 0 && (
              <span className="absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-4 rounded-full bg-red-500 px-[3px] text-[10px] font-medium leading-none text-white">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          <Link href="/m/profile">
            <button 
              className="flex items-center justify-center h-10 w-10 rounded-full bg-blue-600 hover:bg-blue-700 transition-colors"
              data-testid="button-top-profile"
              aria-label="Profile"
            >
              <User className="h-5 w-5 text-white" />
            </button>
          </Link>
        </div>
      </div>

      {/* Lightweight inbox drawer for mobile /m pages */}
      {inboxOpen && (
        <div className="fixed inset-0 z-[70] flex justify-end bg-black/30">
          <div className="h-full w-full max-w-sm bg-white shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div>
                <h2 className="text-sm font-semibold">Notifications</h2>
                <p className="text-xs text-muted-foreground">
                  In-app messages and important updates
                </p>
              </div>
              <Button variant="ghost" size="icon" onClick={() => setInboxOpen(false)}>
                ✕
              </Button>
            </div>
            <MobileInboxList onClose={() => setInboxOpen(false)} onUnreadChange={setUnreadCount} />
          </div>
        </div>
      )}
    </div>
  );
}

interface MobileInboxListProps {
  onClose: () => void;
  onUnreadChange: (count: number) => void;
}

function MobileInboxList({ onClose, onUnreadChange }: MobileInboxListProps) {
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState<
    { id: number; title: string; body: string; isRead: boolean; messageType?: string; createdAt?: string }[]
  >([]);
  const [expandedIds, setExpandedIds] = useState<number[]>([]);

  const fetchMessages = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("GET", "/api/inbox");
      const data = await response.json();
      const items = data.messages || [];
      setMessages(items);
      const unread = items.filter((m: any) => !m.isRead).length;
      onUnreadChange(unread);
      setLoading(false);
    } catch (error) {
      console.error("[MOBILE INBOX] Failed to load messages:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, []);

  const markAsRead = async (ids: number[]) => {
    try {
      if (!ids.length) return;
      await apiRequest("POST", "/api/inbox/mark-read", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      const updated = messages.map((m) =>
        ids.includes(m.id) ? { ...m, isRead: true } : m
      );
      setMessages(updated);
      const unread = updated.filter((m) => !m.isRead).length;
      onUnreadChange(unread);
    } catch (error) {
      console.error("[MOBILE INBOX] Failed to mark messages read:", error);
    }
  };

  const deleteMessages = async (ids: number[]) => {
    try {
      if (!ids.length) return;
      await apiRequest("POST", "/api/inbox/delete", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      const remaining = messages.filter((m) => !ids.includes(m.id));
      setMessages(remaining);
      const unread = remaining.filter((m) => !m.isRead).length;
      onUnreadChange(unread);
    } catch (error) {
      console.error("[MOBILE INBOX] Failed to delete messages:", error);
    }
  };

  const clearInbox = async () => {
    try {
      if (!messages.length) return;
      await apiRequest("POST", "/api/inbox/delete", {
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deleteAll: true }),
      });
      setMessages([]);
      onUnreadChange(0);
    } catch (error) {
      console.error("[MOBILE INBOX] Failed to clear inbox:", error);
    }
  };

  return (
    <div className="flex-1 flex flex-col">
      <div className="flex items-center justify-between px-4 py-2 border-b">
        <Button variant="ghost" size="sm" onClick={fetchMessages}>
          Refresh
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => markAsRead(messages.filter(m => !m.isRead).map(m => m.id))}>
            Mark all as read
          </Button>
          <Button variant="ghost" size="sm" onClick={clearInbox}>
            Clear inbox
          </Button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {loading ? (
          <p className="text-xs text-muted-foreground">Loading messages...</p>
        ) : messages.length === 0 ? (
          <p className="text-xs text-muted-foreground">No notifications yet.</p>
        ) : (
          messages.map((msg) => {
            const isExpanded = expandedIds.includes(msg.id);
            const isLong = (msg.body || "").length > 220;
            const previewBody =
              !isLong || isExpanded ? msg.body : `${msg.body.slice(0, 220)}…`;

            return (
              <div
                key={msg.id}
                className={`rounded-md border px-3 py-2 text-xs ${
                  msg.isRead ? "bg-white" : "bg-primary/5 border-primary/40"
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="font-medium text-sm">{msg.title}</span>
                  {msg.createdAt && (
                    <span className="text-[10px] text-muted-foreground">
                      {new Date(msg.createdAt).toLocaleString()}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-700 whitespace-pre-line">
                  {previewBody}
                </p>
                <div className="mt-1 flex items-center justify-between">
                  {isLong && (
                    <button
                      type="button"
                      className="text-[11px] font-medium text-primary hover:underline"
                      onClick={() => {
                        setExpandedIds((prev) =>
                          isExpanded
                            ? prev.filter((id) => id !== msg.id)
                            : [...prev, msg.id]
                        );
                        if (!msg.isRead) {
                          markAsRead([msg.id]);
                        }
                      }}
                    >
                      {isExpanded ? "Show less" : "Read full message"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="text-[11px] text-muted-foreground hover:text-red-600"
                    onClick={() => deleteMessages([msg.id])}
                  >
                    Clear
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
      <div className="px-4 py-2 border-t mb-24">
        <Button
          variant="outline"
          className="w-full"
          size="sm"
          onClick={onClose}
        >
          Close
        </Button>
      </div>
    </div>
  );
}

export default MobileTopBar;
